import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import ColorThemePreview from '../components/ColorThemePreview';
import { generateThemeFromSeed, serializeTheme } from '../utils/colorGenerator';
import { AVAILABLE_VALUE_ICONS, getIconComponent, getColorClasses } from '../utils/iconMap';
import {
    SaveIcon as Save,
    RefreshIcon as RefreshCw,
    UploadIcon,
    DeleteIcon,
    ArrowLeftIcon as ArrowLeft,
    GlobeIcon as Globe,
    PlusIcon as Plus
} from '../components/Icons';

const DEFAULT_SECTIONS_ORDER = [
    { id: 'intro', enabled: true, name: 'Willkommen & Bund' },
    { id: 'about', enabled: true, name: 'Werte & Über uns' },
    { id: 'groups', enabled: true, name: 'Gruppen & Stufen' },
    { id: 'blog', enabled: true, name: 'Aktuelles / Blog' },
    { id: 'team', enabled: true, name: 'Unser Team' },
    { id: 'events', enabled: true, name: 'Kommende Termine' },
    { id: 'contact', enabled: true, name: 'Kontakt & Schnuppern' }
];

const DEFAULT_MODULES = {
    blog: true,
    events: true,
    groups: true,
    team: true,
    attendance: true,
    tasks: true,
    mailbox: true,
    aiAssistant: true,
    finances: true,
    inventory: true
};

const DEFAULT_FEATURES = {
    bobChat: true,
    eventRegistration: true,
    contactForm: true,
    socialLinks: true,
    bundCard: true,
    cookieConsent: true,
    darkMode: true,
    heroCta: true,
    heroSlideshow: true
};

const DEFAULT_VALUES = [
    {
        icon: 'Leaf',
        title: 'Naturverbundenheit',
        description: 'Wir erkunden die Wildnis, lernen die Natur zu schützen und verbringen unvergessliche Stunden unter freiem Himmel.',
        iconColor: 'primary',
    },
    {
        icon: 'Users',
        title: 'Gemeinschaft & Teamgeist',
        description: 'Gemeinsam meistern wir Herausforderungen, schließen Freundschaften fürs Leben und lernen, uns aufeinander zu verlassen.',
        iconColor: 'secondary',
    },
    {
        icon: 'Sparkles',
        title: 'Persönlichkeitsentwicklung',
        description: 'Wir fördern Selbstständigkeit, Verantwortungsbewusstsein und die Entwicklung neuer Fähigkeiten in jedem Pfadfinder.',
        iconColor: 'tertiary',
    },
    {
        icon: 'Globe',
        title: 'Unabhängigkeit & Offenheit',
        description: 'Wir sind nicht religiös oder parteipolitisch abhängig und für alle offen – jeder ist bei uns willkommen.',
        iconColor: 'primary',
    }
];

const HomepageSettings = () => {
    const navigate = useNavigate();
    const { user, API_URL, getAssetUrl } = useContext(AuthContext);

    // --- Active Tab State ---
    const [activeTab, setActiveTab] = useState('modules');

    // --- Modules & Features State ---
    const [modules, setModules] = useState(DEFAULT_MODULES);
    const [features, setFeatures] = useState(DEFAULT_FEATURES);

    const toggleModule = (moduleKey) => {
        setModules(prev => {
            const nextVal = !prev[moduleKey];
            const sectionIdMap = {
                blog: 'blog',
                events: 'events',
                groups: 'groups',
                team: 'team'
            };
            if (moduleKey in sectionIdMap) {
                const secId = sectionIdMap[moduleKey];
                setSectionsOrder(secPrev => secPrev.map(s => s.id === secId ? { ...s, enabled: nextVal } : s));
            }
            return { ...prev, [moduleKey]: nextVal };
        });
    };

    const toggleFeature = (featureKey) => {
        setFeatures(prev => ({
            ...prev,
            [featureKey]: !prev[featureKey]
        }));
    };

    // --- Sections Order & Visibility ---
    const [sectionsOrder, setSectionsOrder] = useState(DEFAULT_SECTIONS_ORDER);

    // --- Hero Section State ---
    const [hero, setHero] = useState({
        headline: 'Pfadfinder Wutöschingen',
        subheadline: 'Gemeinschaft, Abenteuer und Natur erleben – für Kinder und Jugendliche!',
        primaryCtaText: 'Jetzt Mitglied werden',
        primaryCtaUrl: 'https://on.campflow.de/pfadfinderbund-horizonte/mitgliedsantrag',
        primaryCtaType: 'link',
        secondaryCtaText: 'Direkt reinschnuppern',
        secondaryCtaUrl: '#contact-section',
        secondaryCtaType: 'schnupper'
    });

    // --- Intro & Bund Section State ---
    const [intro, setIntro] = useState({
        title: 'Willkommen bei den Pfadfindern Wutöschingen!',
        subtitle: 'Schön, dass du da bist! Erfahre hier mehr über unseren Stamm und wie du Teil unserer Gemeinschaft werden kannst.',
        mainParagraph1: 'Wir sind ein brandneues Pfadfinderprojekt, das gerade in Wutöschingen entsteht! Als motivierte und engagierte Gruppe können wir es kaum erwarten, gemeinsam mit euch die Natur zu entdecken, neue Freundschaften zu schließen und unvergessliche Abenteuer zu erleben.',
        mainParagraph2: 'Ob Lagerfeuer unter dem Sternenhimmel, spannende Ausflüge in die Natur oder kreative Projekte – bei uns ist für jeden etwas dabei. Sei dabei und gestalte mit uns die Zukunft der Pfadfinder in unserer Gemeinde!',
        bundCard: {
            show: true,
            logoUrl: 'https://wsrv.nl/?url=https%3A%2F%2Fpbhorizonte.de%2Fwp-content%2Fuploads%2F2021%2F02%2Fcropped-PBH-freigestellt.png&w=300&output=webp',
            title: 'Pfadfinderbund Horizonte e.V.',
            description1: 'Der Pfadfinderbund Horizonte e.V. ist ein ehrenamtlicher Verein, der seit über 50 Jahren erfolgreiche Jugendarbeit in Baden-Württemberg leistet. Mit Gruppen in bereits vier Orten sind wir Teil einer großen Pfadfinder-Familie. Die Neugründung in Wutöschingen markiert einen aufregenden neuen Meilenstein – und du kannst von Anfang an dabei sein!',
            description2: 'Unsere Ziele: Abenteuer erleben, Verantwortung übernehmen, Gemeinschaft stärken und die Natur schützen. Gemeinsam schaffen wir einen Ort, an dem junge Menschen wachsen und fürs Leben lernen können.',
            buttonText: 'Mehr über den Bund erfahren',
            buttonUrl: 'https://pbhorizonte.de/'
        }
    });

    // --- About & Values Section State ---
    const [about, setAbout] = useState({
        title: 'Was Pfadfinden bei uns bedeutet',
        subtitle: 'Unsere Werte und Ziele: Entdecke, was uns antreibt und wie wir gemeinsam wachsen.',
        values: DEFAULT_VALUES
    });

    // --- Section Headers State ---
    const [sectionHeaders, setSectionHeaders] = useState({
        groups: {
            title: 'Unsere Gruppen & Stufen',
            subtitle: 'Finde die passende Gruppe für dein Alter – von den Jüngsten bis zu den Erwachsenen.'
        },
        blog: {
            title: 'Aktuelles aus dem Stamm',
            subtitle: 'Hier findest du Berichte, Ankündigungen und Einblicke in unsere Gruppenstunden und Aktionen.'
        },
        team: {
            title: 'Unser Team',
            subtitle: 'Lerne die Menschen hinter dem Projekt kennen, die mit Herzblut und Erfahrung unsere Abenteuer begleiten.'
        },
        events: {
            title: 'Kommende Veranstaltungen',
            subtitle: 'Hier findest du alle anstehenden Termine, Aktionen und Lager.'
        },
        contact: {
            title: 'Kontakt & Schnuppern',
            subtitle: 'Du hast Fragen oder möchtest bei einer Gruppenstunde reinschnuppern? Schreib uns einfach!'
        }
    });

    // --- Organization / White-label Identity State ---
    const [organization, setOrganization] = useState({
        stammName: 'Pfadfinder Wutöschingen',
        stammFullName: 'Pfadfinder Wutöschingen',
        bundName: 'Pfadfinderbund Horizonte e.V.',
        dachverband: 'DPV - Deutscher Pfadfinderverband',
        location: 'Wutöschingen',
        locationBadge: 'Wutöschingen, DE',
        motto: 'Natur erleben, Freunde finden, fürs Leben lernen.',
        registrationUrl: 'https://on.campflow.de/pfadfinderbund-horizonte/mitgliedsantrag',
        instagramUrl: 'https://www.instagram.com/pfadfinder_wutoeschingen/',
        whatsappUrl: 'https://chat.whatsapp.com/Ewa1whIq0Fc2B328CD593z',
        contactEmail: 'info@pfadfinder-wutoeschingen.de',
        bundUrl: 'https://pbhorizonte.de/',
        bundLogoUrl: 'https://wsrv.nl/?url=https%3A%2F%2Fpbhorizonte.de%2Fwp-content%2Fuploads%2F2021%2F02%2Fcropped-PBH-freigestellt.png&w=300&output=webp',
        footerText: 'Wir entdecken die Welt, schützen die Natur und stärken die Gemeinschaft. Komm vorbei und erlebe dein nächstes Abenteuer mit uns!',
        footerTagline: 'Handgemacht mit ❤️ für die Jugend von der Jugend.',
        faviconUrl: '',
        tabTitle: 'Pfadfinder Wutöschingen',
        dashboardTabTitle: 'Dashboard | Pfadfinder Wutöschingen',
        dashboardWelcomeSubtitle: 'Eingeloggt als'
    });

    // --- Image Slideshow State ---
    const [heroImages, setHeroImages] = useState([]);
    const [slideshowInterval, setSlideshowInterval] = useState(6);

    // --- Color Theme State ---
    const [seedColor, setSeedColor] = useState('#009640');
    const [generatedTheme, setGeneratedTheme] = useState(null);
    const [colorPreviewMode, setColorPreviewMode] = useState('light');
    const [savedTheme, setSavedTheme] = useState(null);

    // --- UI State ---
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isUploadingFavicon, setIsUploadingFavicon] = useState(false);
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);

    // --- Image Sources ---
    const [uploadedImages, setUploadedImages] = useState([]);
    const [uploadedSearch, setUploadedSearch] = useState('');
    const [isLoadingUploadedImages, setIsLoadingUploadedImages] = useState(false);
    const [blogImages, setBlogImages] = useState([]);
    const [blogSearch, setBlogSearch] = useState('');
    const [isLoadingBlogImages, setIsLoadingBlogImages] = useState(false);

    // --- Value Card Modal / Edit State ---
    const [editingValueIndex, setEditingValueIndex] = useState(null);

    // Fetch settings on mount
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/settings/homepage`);
                if (data.heroImages && Array.isArray(data.heroImages)) {
                    setHeroImages(data.heroImages);
                } else if (data.heroImage) {
                    setHeroImages([data.heroImage]);
                }
                if (data.slideshowInterval) setSlideshowInterval(data.slideshowInterval);
                if (data.sectionsOrder && Array.isArray(data.sectionsOrder)) setSectionsOrder(data.sectionsOrder);
                if (data.hero) setHero(prev => ({ ...prev, ...data.hero }));
                if (data.intro) {
                    setIntro(prev => ({
                        ...prev,
                        ...data.intro,
                        bundCard: { ...prev.bundCard, ...(data.intro.bundCard || {}) }
                    }));
                }
                if (data.about) {
                    setAbout(prev => ({
                        ...prev,
                        ...data.about,
                        values: Array.isArray(data.about.values) && data.about.values.length > 0 ? data.about.values : prev.values
                    }));
                }
                if (data.sectionHeaders) {
                    setSectionHeaders(prev => ({
                        ...prev,
                        ...data.sectionHeaders,
                        blog: { ...prev.blog, ...(data.sectionHeaders.blog || {}) },
                        team: { ...prev.team, ...(data.sectionHeaders.team || {}) },
                        events: { ...prev.events, ...(data.sectionHeaders.events || {}) },
                        contact: { ...prev.contact, ...(data.sectionHeaders.contact || {}) }
                    }));
                }
                if (data.modules) setModules(prev => ({ ...prev, ...data.modules }));
                if (data.features) setFeatures(prev => ({ ...prev, ...data.features }));
                if (data.organization) setOrganization(prev => ({ ...prev, ...data.organization }));
                if (data.colorTheme) {
                    setSavedTheme(data.colorTheme);
                    setSeedColor(data.colorTheme.seed || '#009640');
                    try {
                        const theme = generateThemeFromSeed(data.colorTheme.seed || '#009640');
                        setGeneratedTheme(theme);
                    } catch (e) { /* ignore */ }
                }
            } catch (err) {
                console.error('Failed to fetch homepage settings', err);
                if (err.response?.status !== 404) {
                    setMessage('Fehler beim Laden der Einstellungen.');
                    setIsError(true);
                }
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, [API_URL]);

    // Fetch uploaded images — only when images tab is active
    useEffect(() => {
        if (activeTab !== 'images') return;
        const fetchUploadedImages = async () => {
            if (!user?.token) return;
            setIsLoadingUploadedImages(true);
            try {
                const res = await axios.get(`${API_URL}/files`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                    params: { search: uploadedSearch || undefined },
                });
                setUploadedImages(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error('Failed to load uploaded images', err);
            } finally {
                setIsLoadingUploadedImages(false);
            }
        };
        fetchUploadedImages();
    }, [API_URL, user?.token, uploadedSearch, activeTab]);

    // Fetch blog images — only when images tab is active
    useEffect(() => {
        if (activeTab !== 'images') return;
        const fetchBlogImages = async () => {
            if (!user?.token) return;
            setIsLoadingBlogImages(true);
            try {
                const res = await axios.get(`${API_URL}/blog/manage`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                    params: { imageOnly: true, search: blogSearch || undefined },
                });
                const allPosts = Array.isArray(res.data) ? res.data : [];
                const filtered = blogSearch
                    ? allPosts.filter(p => p.imageUrl && `${p.title || ''} ${p.content || ''}`.toLowerCase().includes(blogSearch.toLowerCase()))
                    : allPosts.filter(p => p.imageUrl);
                setBlogImages(filtered);
            } catch (err) {
                console.error('Failed to load blog images', err);
            } finally {
                setIsLoadingBlogImages(false);
            }
        };
        fetchBlogImages();
    }, [API_URL, user?.token, blogSearch, activeTab]);

    // --- Color generation ---
    const handleGenerateColors = useCallback(() => {
        try {
            const theme = generateThemeFromSeed(seedColor);
            setGeneratedTheme(theme);
            setMessage('Farbschema generiert! Vorschau unten.');
            setIsError(false);
        } catch (err) {
            console.error('Color generation failed', err);
            setMessage('Fehler bei der Farbgenerierung.');
            setIsError(true);
        }
    }, [seedColor]);

    // --- Image selection ---
    const toggleImageSelection = (url) => {
        setHeroImages(prev => {
            if (prev.includes(url)) return prev.filter(u => u !== url);
            return [...prev, url];
        });
        setMessage('');
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setIsUploading(true);
        setMessage('');
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await axios.post(`${API_URL}/files/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${user.token}` },
            });
            const uploadedUrl = res.data.url;
            setHeroImages(prev => [...prev, uploadedUrl]);
            setMessage('Bild hochgeladen und hinzugefügt!');
            setIsError(false);
            setUploadedImages(prev => [
                { key: res.data.key, name: file.name, url: uploadedUrl, size: file.size },
                ...prev.filter(item => item.url !== uploadedUrl)
            ]);
        } catch (error) {
            setMessage(error.response?.data?.message || 'Fehler beim Hochladen.');
            setIsError(true);
        } finally {
            setIsUploading(false);
        }
    };

    const handleFaviconUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setIsUploadingFavicon(true);
        setMessage('');
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await axios.post(`${API_URL}/files/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${user.token}` },
            });
            const uploadedUrl = res.data.url;
            setOrganization(prev => ({ ...prev, faviconUrl: uploadedUrl }));
            setMessage('Favicon / Tab-Logo erfolgreich hochgeladen!');
            setIsError(false);
        } catch (error) {
            setMessage(error.response?.data?.message || 'Fehler beim Hochladen des Favicons.');
            setIsError(true);
        } finally {
            setIsUploadingFavicon(false);
        }
    };

    // --- Section Reorder Helpers ---
    const moveSection = (index, direction) => {
        const newOrder = [...sectionsOrder];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= newOrder.length) return;
        const temp = newOrder[index];
        newOrder[index] = newOrder[targetIndex];
        newOrder[targetIndex] = temp;
        setSectionsOrder(newOrder);
    };

    const toggleSectionEnabled = (index) => {
        const newOrder = [...sectionsOrder];
        newOrder[index] = { ...newOrder[index], enabled: !newOrder[index].enabled };
        setSectionsOrder(newOrder);
    };

    // --- Value Card Helpers ---
    const moveValueCard = (index, direction) => {
        const newValues = [...about.values];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= newValues.length) return;
        const temp = newValues[index];
        newValues[index] = newValues[targetIndex];
        newValues[targetIndex] = temp;
        setAbout(prev => ({ ...prev, values: newValues }));
    };

    const addValueCard = () => {
        const newCard = {
            icon: 'Leaf',
            title: 'Neuer Wert',
            description: 'Beschreibung des Wertes oder Zieles unseres Stammes...',
            iconColor: 'primary'
        };
        setAbout(prev => ({ ...prev, values: [...prev.values, newCard] }));
        setEditingValueIndex(about.values.length);
    };

    const deleteValueCard = (index) => {
        setAbout(prev => ({ ...prev, values: prev.values.filter((_, i) => i !== index) }));
        if (editingValueIndex === index) setEditingValueIndex(null);
    };

    const updateValueCard = (index, field, val) => {
        const newValues = [...about.values];
        newValues[index] = { ...newValues[index], [field]: val };
        setAbout(prev => ({ ...prev, values: newValues }));
    };

    // --- Save Everything ---
    const handleSave = async () => {
        setIsSubmitting(true);
        setMessage('');
        setIsError(false);
        try {
            const body = {
                heroImages,
                heroImage: heroImages[0] || '',
                slideshowInterval,
                sectionsOrder,
                modules,
                features,
                hero,
                intro,
                about,
                sectionHeaders,
                organization
            };
            if (generatedTheme) {
                body.colorTheme = serializeTheme(generatedTheme);
            } else if (savedTheme) {
                body.colorTheme = savedTheme;
            }
            await axios.put(`${API_URL}/settings/homepage`, body, {
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
            });
            if (generatedTheme) setSavedTheme(serializeTheme(generatedTheme));
            setMessage('Einstellungen & Module erfolgreich gespeichert!');
            setIsError(false);
        } catch (error) {
            setMessage(error.response?.data?.message || 'Fehler beim Speichern.');
            setIsError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    const tabs = [
        { id: 'modules', label: 'Module & Funktionen', desc: 'Kernmodule & Systemfunktionen' },
        { id: 'sections', label: 'Sektionen & Anordnung', desc: 'Reihenfolge & Sichtbarkeit' },
        { id: 'hero', label: 'Hero & Start', desc: 'Titel & Call-to-Actions' },
        { id: 'intro', label: 'Einleitung & Bund', desc: 'Willkommen & Partnerkarte' },
        { id: 'about', label: 'Werte & Über uns', desc: 'Werte-Karten & Icons' },
        { id: 'headers', label: 'Sektionstitel', desc: 'Überschriften der Bereiche' },
        { id: 'org', label: 'Stamm & Organisation', desc: 'White-Label & Kontaktdaten' },
        { id: 'images', label: 'Bilder & Diashow', desc: 'Hintergrundbilder' },
        { id: 'colors', label: 'Farbgenerator', desc: 'Material You Theme' },
    ];

    return (
        <div className="min-h-screen bg-surface text-surface-on pb-24 animate-fade-in">
            {/* Header */}
            <header className="sticky top-0 z-50 bg-surface/90 backdrop-blur-md border-b border-outlineVariant/40 px-4 py-4 md:px-8">
                <div className="flex items-center justify-between max-w-7xl mx-auto">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate('/admin-tools')} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-surface-on/5 text-surface-on transition-colors shrink-0">
                            <ArrowLeft sx={{ fontSize: 24 }} />
                        </button>
                        <div>
                            <h1 className="text-title-large font-bold text-surface-on">Startseiten-Verwaltung</h1>
                            <p className="text-label-small text-surface-onVariant uppercase tracking-widest font-bold opacity-70">
                                Inhalte, Anordnung & Stammes-Konfiguration
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        {message && (
                            <span className={`text-label-medium font-bold animate-fade-in ${isError ? 'text-error' : 'text-primary'}`}>
                                {isError ? '⚠️' : '✓'} {message}
                            </span>
                        )}
                        <button onClick={handleSave} disabled={isSubmitting} className="md3-btn-filled flex items-center gap-2 shadow-elevation-2">
                            {isSubmitting ? <RefreshCw className="animate-spin" sx={{ fontSize: 18 }} /> : <Save sx={{ fontSize: 18 }} />}
                            <span>Speichern</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* Tab Navigation */}
            <div className="max-w-7xl mx-auto px-4 md:px-8 pt-6">
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-4 py-2 rounded-2xl text-label-large font-medium transition-all shrink-0 flex items-center gap-2 ${
                                activeTab === tab.id
                                    ? 'bg-primary text-primary-on shadow-elevation-1'
                                    : 'bg-surface-containerHigh text-surface-on hover:bg-surface-containerHighest'
                            }`}
                        >
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Content Area */}
            <main className="max-w-7xl mx-auto px-4 md:px-8 mt-6">

                {/* ============================================================ */}
                {/* 0. MODULES & FEATURES MATRIX TAB */}
                {/* ============================================================ */}
                {activeTab === 'modules' && (
                    <div className="space-y-8 animate-fade-in max-w-6xl">
                        {/* Header Banner */}
                        <div className="p-6 md:p-8 rounded-3xl bg-surface-containerLow border border-outlineVariant/30 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div>
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-container text-primary-onContainer text-xs font-bold uppercase tracking-wider mb-2">
                                    <Globe style={{ fontSize: 16 }} />
                                    <span>Modulare White-Label Steuerung</span>
                                </div>
                                <h3 className="text-title-large font-bold text-surface-on">System-Module & Funktionen</h3>
                                <p className="text-body-medium text-surface-onVariant mt-1 max-w-2xl">
                                    Schalte Kern-Module und Website-Funktionen flexibel an oder aus. Deaktivierte Funktionen werden automatisch aus der Navigation und dem Dashboard entfernt.
                                </p>
                            </div>
                        </div>

                        {/* Section 1: Kern-Module */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <span className="w-8 h-8 rounded-xl bg-primary-container text-primary-onContainer flex items-center justify-center font-bold text-sm">
                                    🚀
                                </span>
                                <div>
                                    <h4 className="text-title-medium font-bold text-surface-on">Kernmodule (Dashboard & Website)</h4>
                                    <p className="text-body-small text-surface-onVariant">Hauptbereiche des Pfadfinder-Systems</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* 1. Blog */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${modules.blog ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div className="flex items-start gap-3.5">
                                        <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-xl shrink-0">
                                            📰
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h5 className="text-title-small font-bold text-surface-on">Blog & Berichte</h5>
                                                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-surface-containerHighest text-surface-onVariant">Website + Dash</span>
                                            </div>
                                            <p className="text-body-small text-surface-onVariant mt-1">
                                                Artikel verfassen, Berichte & Neuigkeiten auf der Website veröffentlichen.
                                            </p>
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={modules.blog}
                                            onChange={() => toggleModule('blog')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {/* 2. Events */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${modules.events ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div className="flex items-start gap-3.5">
                                        <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-xl shrink-0">
                                            📅
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h5 className="text-title-small font-bold text-surface-on">Events & Veranstaltungen</h5>
                                                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-surface-containerHighest text-surface-onVariant">Website + Dash</span>
                                            </div>
                                            <p className="text-body-small text-surface-onVariant mt-1">
                                                Termine, Lager & Aktionen planen, Detailseiten & Kalenderübersichten.
                                            </p>
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={modules.events}
                                            onChange={() => toggleModule('events')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {/* 3. Groups */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${modules.groups ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div className="flex items-start gap-3.5">
                                        <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-xl shrink-0">
                                            🏕️
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h5 className="text-title-small font-bold text-surface-on">Gruppen & Altersstufen</h5>
                                                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-surface-containerHighest text-surface-onVariant">Website + Dash</span>
                                            </div>
                                            <p className="text-body-small text-surface-onVariant mt-1">
                                                Präsentation von Stufen (Wölflinge, Jupfis, Pfadis, Rover) mit Motto, Wappen & Treffzeiten.
                                            </p>
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={modules.groups}
                                            onChange={() => toggleModule('groups')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {/* 4. Team */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${modules.team ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div className="flex items-start gap-3.5">
                                        <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-xl shrink-0">
                                            👥
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h5 className="text-title-small font-bold text-surface-on">Team & Leitungsprofile</h5>
                                                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-surface-containerHighest text-surface-onVariant">Website + Dash</span>
                                            </div>
                                            <p className="text-body-small text-surface-onVariant mt-1">
                                                Öffentliche Profile für Gruppenleiter und Teammitglieder auf der Homepage.
                                            </p>
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={modules.team}
                                            onChange={() => toggleModule('team')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {/* 5. Attendance */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${modules.attendance ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div className="flex items-start gap-3.5">
                                        <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-xl shrink-0">
                                            📋
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h5 className="text-title-small font-bold text-surface-on">Anwesenheitsliste</h5>
                                                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-surface-containerHighest text-surface-onVariant">Dashboard</span>
                                            </div>
                                            <p className="text-body-small text-surface-onVariant mt-1">
                                                Teilnehmererfassung für wöchentliche Gruppenstunden und Aktionen.
                                            </p>
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={modules.attendance}
                                            onChange={() => toggleModule('attendance')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {/* 6. Tasks */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${modules.tasks ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div className="flex items-start gap-3.5">
                                        <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-xl shrink-0">
                                            ✅
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h5 className="text-title-small font-bold text-surface-on">Aufgaben & Tasks</h5>
                                                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-surface-containerHighest text-surface-onVariant">Dashboard</span>
                                            </div>
                                            <p className="text-body-small text-surface-onVariant mt-1">
                                                Aufgabenverwaltung, Zuweisungen zu Events & Gruppen und Fristen im Dashboard.
                                            </p>
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={modules.tasks}
                                            onChange={() => toggleModule('tasks')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {/* 7. Mailbox */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${modules.mailbox ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div className="flex items-start gap-3.5">
                                        <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-xl shrink-0">
                                            ✉️
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h5 className="text-title-small font-bold text-surface-on">Mailbox & E-Mail-Routings</h5>
                                                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-surface-containerHighest text-surface-onVariant">Dashboard</span>
                                            </div>
                                            <p className="text-body-small text-surface-onVariant mt-1">
                                                Offizielle Stammes-E-Mail-Adressen und Weiterleitungen über Cloudflare.
                                            </p>
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={modules.mailbox}
                                            onChange={() => toggleModule('mailbox')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {/* 8. AI Assistant */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${modules.aiAssistant ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div className="flex items-start gap-3.5">
                                        <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-xl shrink-0">
                                            🤖
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h5 className="text-title-small font-bold text-surface-on">KI-Assistenten (Bob & Devino)</h5>
                                                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-surface-containerHighest text-surface-onVariant">System</span>
                                            </div>
                                            <p className="text-body-small text-surface-onVariant mt-1">
                                                KI-Chatbots auf der Website und Assistenzfunktionen im Dashboard.
                                            </p>
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={modules.aiAssistant}
                                            onChange={() => toggleModule('aiAssistant')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {/* 9. Finances & Kassenbuch */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${modules.finances ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div className="flex items-start gap-3.5">
                                        <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-xl shrink-0">
                                            💰
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h5 className="text-title-small font-bold text-surface-on">Kassenbuch & Finanzen</h5>
                                                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-surface-containerHighest text-surface-onVariant">Kassenwart</span>
                                            </div>
                                            <p className="text-body-small text-surface-onVariant mt-1">
                                                Kassenbuch-Verwaltung, Einnahmen/Ausgaben, Belege und Kontenstände.
                                            </p>
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={modules.finances}
                                            onChange={() => toggleModule('finances')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {/* 10. Material & Inventory */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${modules.inventory ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div className="flex items-start gap-3.5">
                                        <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-xl shrink-0">
                                            🏕️
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h5 className="text-title-small font-bold text-surface-on">Material & Inventar</h5>
                                                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-surface-containerHighest text-surface-onVariant">Materialwart</span>
                                            </div>
                                            <p className="text-body-small text-surface-onVariant mt-1">
                                                Material-Tracking, Lagerorte (auch dezentral privat), Fremdmaterial, Leihgaben und Reparaturen.
                                            </p>
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={modules.inventory}
                                            onChange={() => toggleModule('inventory')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Section 2: Frontend & Website Features */}
                        <div className="space-y-4 pt-4">
                            <div className="flex items-center gap-2">
                                <span className="w-8 h-8 rounded-xl bg-secondary-container text-secondary-onContainer flex items-center justify-center font-bold text-sm">
                                    🌐
                                </span>
                                <div>
                                    <h4 className="text-title-medium font-bold text-surface-on">Website- & Interaktions-Funktionen</h4>
                                    <p className="text-body-small text-surface-onVariant">Feinsteuerung von Besucher-Features auf der Homepage</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Feature 1: Bob Chatbot */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${features.bobChat ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h5 className="text-title-small font-bold text-surface-on">Bob Chat-Widget (Website)</h5>
                                        </div>
                                        <p className="text-body-small text-surface-onVariant mt-1">
                                            Schwebender Chat-Button unten rechts für direkte Besucher-Fragen.
                                        </p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={features.bobChat}
                                            onChange={() => toggleFeature('bobChat')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {/* Feature 2: Event Registration */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${features.eventRegistration ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h5 className="text-title-small font-bold text-surface-on">Öffentliche Event-Anmeldung</h5>
                                        </div>
                                        <p className="text-body-small text-surface-onVariant mt-1">
                                            Gäste & Interessenten können sich online für Veranstaltungen anmelden.
                                        </p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={features.eventRegistration}
                                            onChange={() => toggleFeature('eventRegistration')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {/* Feature 3: Contact Form */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${features.contactForm ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h5 className="text-title-small font-bold text-surface-on">Kontakt- & Schnupperformular</h5>
                                        </div>
                                        <p className="text-body-small text-surface-onVariant mt-1">
                                            Direktes Formular auf der Website zum Absenden von Anfragen.
                                        </p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={features.contactForm}
                                            onChange={() => toggleFeature('contactForm')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {/* Feature 4: Social Media Links */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${features.socialLinks ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h5 className="text-title-small font-bold text-surface-on">Social Media (Instagram & WhatsApp)</h5>
                                        </div>
                                        <p className="text-body-small text-surface-onVariant mt-1">
                                            Verlinkung zu Instagram und zur WhatsApp-Community im Footer/Kopfbereich.
                                        </p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={features.socialLinks}
                                            onChange={() => toggleFeature('socialLinks')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {/* Feature 5: Bund Card */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${features.bundCard ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h5 className="text-title-small font-bold text-surface-on">Bund- & Partner-Karte</h5>
                                        </div>
                                        <p className="text-body-small text-surface-onVariant mt-1">
                                            Hervorgehobene Partnerkarte des Dachverbandes in der Einleitung.
                                        </p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={features.bundCard}
                                            onChange={() => toggleFeature('bundCard')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {/* Feature 6: Cookie Consent */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${features.cookieConsent ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h5 className="text-title-small font-bold text-surface-on">Cookie- & Datenschutzbanner</h5>
                                        </div>
                                        <p className="text-body-small text-surface-onVariant mt-1">
                                            DSGVO-Einwilligungsbanner für neue Besucher auf der Website.
                                        </p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={features.cookieConsent}
                                            onChange={() => toggleFeature('cookieConsent')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {/* Feature 7: Dark Mode */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${features.darkMode ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h5 className="text-title-small font-bold text-surface-on">Dark / Light Mode Umschalter</h5>
                                        </div>
                                        <p className="text-body-small text-surface-onVariant mt-1">
                                            Ermöglicht Benutzern das Umschalten zwischen Hell- und Dunkeldesign.
                                        </p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={features.darkMode}
                                            onChange={() => toggleFeature('darkMode')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {/* Feature 8: Hero Slideshow */}
                                <div className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${features.heroSlideshow ? 'bg-surface-container border-outlineVariant/40 shadow-sm' : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'}`}>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h5 className="text-title-small font-bold text-surface-on">Hero Diashow (Bilder-Wechsel)</h5>
                                        </div>
                                        <p className="text-body-small text-surface-onVariant mt-1">
                                            Automatischer Wechsel mehrerer Hintergrundbilder im Kopfbereich.
                                        </p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={features.heroSlideshow}
                                            onChange={() => toggleFeature('heroSlideshow')}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ============================================================ */}
                {/* 1. SECTIONS ORDER TAB */}
                {/* ============================================================ */}
                {activeTab === 'sections' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-6">
                            <section className="md3-card-elevated overflow-hidden bg-surface-containerLowest border border-outlineVariant/30 p-6">
                                <div className="mb-6">
                                    <h3 className="text-title-medium font-bold text-surface-on">Sektionen anordnen & steuern</h3>
                                    <p className="text-body-small text-surface-onVariant mt-1">
                                        Verschiebe Sektionen nach oben oder unten, um deren Reihenfolge auf der Startseite anzupassen. Mit dem Schalter kannst du einzelne Sektionen temporär ausblenden.
                                    </p>
                                </div>

                                <div className="space-y-3">
                                    {/* Hero Section (Always on Top) */}
                                    <div className="flex items-center justify-between p-4 rounded-2xl bg-surface-containerLow border border-outlineVariant/30 opacity-80">
                                        <div className="flex items-center gap-3">
                                            <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">
                                                Top
                                            </span>
                                            <div>
                                                <h4 className="text-title-small font-bold">Hero Section (Kopfbereich)</h4>
                                                <p className="text-body-small text-surface-onVariant">Hintergrundbild / Diashow, Haupttitel & CTA-Buttons</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setActiveTab('hero')}
                                            className="md3-btn-tonal text-xs py-1.5 px-3 rounded-full"
                                        >
                                            Bearbeiten
                                        </button>
                                    </div>

                                    {/* Dynamic Sections */}
                                    {sectionsOrder.map((sec, idx) => (
                                        <div
                                            key={sec.id}
                                            className={`flex items-center justify-between p-4 rounded-2xl border transition-all ${
                                                sec.enabled
                                                    ? 'bg-surface-container border-outlineVariant/40 shadow-sm'
                                                    : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="w-8 h-8 rounded-full bg-surface-containerHighest flex items-center justify-center font-bold text-xs text-surface-on">
                                                    {idx + 1}
                                                </span>
                                                <div>
                                                    <h4 className="text-title-small font-bold text-surface-on flex items-center gap-2">
                                                        {sec.name}
                                                        {!sec.enabled && (
                                                            <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-error-container text-error-onContainer">
                                                                Ausgeblendet
                                                            </span>
                                                        )}
                                                    </h4>
                                                    <p className="text-body-small text-surface-onVariant">ID: #{sec.id}-section</p>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                {/* Reorder Buttons */}
                                                <button
                                                    onClick={() => moveSection(idx, -1)}
                                                    disabled={idx === 0}
                                                    className="w-9 h-9 rounded-full flex items-center justify-center bg-surface-containerHigh hover:bg-surface-containerHighest disabled:opacity-30 transition-colors"
                                                    title="Nach oben verschieben"
                                                >
                                                    ▲
                                                </button>
                                                <button
                                                    onClick={() => moveSection(idx, 1)}
                                                    disabled={idx === sectionsOrder.length - 1}
                                                    className="w-9 h-9 rounded-full flex items-center justify-center bg-surface-containerHigh hover:bg-surface-containerHighest disabled:opacity-30 transition-colors"
                                                    title="Nach unten verschieben"
                                                >
                                                    ▼
                                                </button>

                                                {/* Visibility Toggle */}
                                                <button
                                                    onClick={() => toggleSectionEnabled(idx)}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                                                        sec.enabled
                                                            ? 'bg-primary/10 text-primary hover:bg-primary/20'
                                                            : 'bg-surface-containerHighest text-surface-onVariant hover:text-surface-on'
                                                    }`}
                                                >
                                                    {sec.enabled ? 'Sichtbar' : 'Versteckt'}
                                                </button>

                                                {/* Jump to Edit */}
                                                {sec.id === 'intro' && (
                                                    <button onClick={() => setActiveTab('intro')} className="md3-btn-tonal text-xs py-1.5 px-3 rounded-full">
                                                        Edit
                                                    </button>
                                                )}
                                                {sec.id === 'about' && (
                                                    <button onClick={() => setActiveTab('about')} className="md3-btn-tonal text-xs py-1.5 px-3 rounded-full">
                                                        Edit
                                                    </button>
                                                )}
                                                {(sec.id === 'blog' || sec.id === 'team' || sec.id === 'events' || sec.id === 'contact') && (
                                                    <button onClick={() => setActiveTab('headers')} className="md3-btn-tonal text-xs py-1.5 px-3 rounded-full">
                                                        Titel
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        </div>

                        {/* Sidebar Info */}
                        <div className="space-y-6">
                            <section className="md3-card-filled p-6 bg-secondary-container/30 text-secondary-onContainer rounded-3xl">
                                <h4 className="text-title-small font-bold mb-2">Flexibles Baukasten-System</h4>
                                <p className="text-body-small leading-relaxed">
                                    Jeder Pfadfinderstamm kann die Sektionen nach Bedarf anpassen. Wenn dein Stamm beispielsweise noch keine Blogbeiträge oder kein Teamfoto hat, deaktiviere die Sektion einfach mit einem Klick!
                                </p>
                            </section>
                        </div>
                    </div>
                )}

                {/* ============================================================ */}
                {/* 2. HERO SECTION TAB */}
                {/* ============================================================ */}
                {activeTab === 'hero' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-6">
                            <section className="md3-card-elevated p-6 bg-surface-containerLowest border border-outlineVariant/30 rounded-3xl space-y-6">
                                <div>
                                    <h3 className="text-title-medium font-bold text-surface-on">Hero-Kopfbereich anpassen</h3>
                                    <p className="text-body-small text-surface-onVariant mt-1">
                                        Der erste Eindruck auf der Startseite: Titel, Slogan und Action-Buttons.
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="text-label-medium text-surface-onVariant font-bold block mb-1">
                                            Hauptüberschrift (Headline)
                                        </label>
                                        <input
                                            type="text"
                                            value={hero.title || ''}
                                            onChange={e => setHero(prev => ({ ...prev, title: e.target.value }))}
                                            className="md3-textfield-outlined w-full text-body-large"
                                            placeholder="z. B. Entdecke die Welt mit den Pfadfindern!"
                                        />
                                    </div>

                                    <div>
                                        <label className="text-label-medium text-surface-onVariant font-bold block mb-1">
                                            Untertitel / Slogan
                                        </label>
                                        <input
                                            type="text"
                                            value={hero.subtitle || ''}
                                            onChange={e => setHero(prev => ({ ...prev, subtitle: e.target.value }))}
                                            className="md3-textfield-outlined w-full text-body-medium"
                                            placeholder="z. B. Natur erleben, Freunde finden, fürs Leben lernen."
                                        />
                                    </div>

                                    <div className="pt-4 border-t border-outlineVariant/30 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Primary CTA */}
                                        <div className="p-4 rounded-2xl bg-surface-containerHigh/50 border border-outlineVariant/30 space-y-3">
                                            <h4 className="text-title-small font-bold text-primary">Primärer Button (Rechts)</h4>
                                            <div>
                                                <label className="text-label-small text-surface-onVariant block mb-1 font-bold">Button-Text</label>
                                                <input
                                                    type="text"
                                                    value={hero.primaryCtaText || ''}
                                                    onChange={e => setHero(prev => ({ ...prev, primaryCtaText: e.target.value }))}
                                                    className="md3-textfield-outlined w-full text-body-small"
                                                    placeholder="Hier anmelden"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-label-small text-surface-onVariant block mb-1 font-bold">Link URL oder Anker</label>
                                                <input
                                                    type="text"
                                                    value={hero.primaryCtaUrl || ''}
                                                    onChange={e => setHero(prev => ({ ...prev, primaryCtaUrl: e.target.value }))}
                                                    className="md3-textfield-outlined w-full text-body-small font-mono text-xs"
                                                    placeholder="https://on.campflow.de/..."
                                                />
                                            </div>
                                        </div>

                                        {/* Secondary CTA */}
                                        <div className="p-4 rounded-2xl bg-surface-containerHigh/50 border border-outlineVariant/30 space-y-3">
                                            <h4 className="text-title-small font-bold text-secondary">Sekundärer Button (Links)</h4>
                                            <div>
                                                <label className="text-label-small text-surface-onVariant block mb-1 font-bold">Button-Text</label>
                                                <input
                                                    type="text"
                                                    value={hero.secondaryCtaText || ''}
                                                    onChange={e => setHero(prev => ({ ...prev, secondaryCtaText: e.target.value }))}
                                                    className="md3-textfield-outlined w-full text-body-small"
                                                    placeholder="Direkt reinschnuppern"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-label-small text-surface-onVariant block mb-1 font-bold">Aktion</label>
                                                <select
                                                    value={hero.secondaryCtaType || 'schnupper'}
                                                    onChange={e => setHero(prev => ({ ...prev, secondaryCtaType: e.target.value }))}
                                                    className="md3-textfield-outlined w-full text-body-small"
                                                >
                                                    <option value="schnupper">Schnupper-Wizard im Kontaktformular öffnen</option>
                                                    <option value="scroll">Zu Kontakt scrollen (#contact-section)</option>
                                                    <option value="link">Benutzerdefinierte URL öffnen</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>

                        {/* Sidebar Info */}
                        <div className="space-y-6">
                            <section className="md3-card-filled p-6 bg-secondary-container/30 text-secondary-onContainer rounded-3xl">
                                <h4 className="text-title-small font-bold mb-2">Tipp für Diashow-Bilder</h4>
                                <p className="text-body-small leading-relaxed">
                                    Die Hintergrundbilder und das Wechselintervall für den Hero-Bereich kannst du im Tab <strong>🖼️ Bilder & Diashow</strong> einstellen.
                                </p>
                            </section>
                        </div>
                    </div>
                )}

                {/* ============================================================ */}
                {/* 3. INTRO & BUND SECTION TAB */}
                {/* ============================================================ */}
                {activeTab === 'intro' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-6">
                            {/* Willkommen & Intro */}
                            <section className="md3-card-elevated p-6 bg-surface-containerLowest border border-outlineVariant/30 rounded-3xl space-y-6">
                                <div>
                                    <h3 className="text-title-medium font-bold text-surface-on">Willkommen & Einleitung</h3>
                                    <p className="text-body-small text-surface-onVariant mt-1">
                                        Die Willkommenskarte direkt unter der Navigationsleiste.
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="text-label-medium text-surface-onVariant font-bold block mb-1">
                                            Sektionstitel
                                        </label>
                                        <input
                                            type="text"
                                            value={intro.title || ''}
                                            onChange={e => setIntro(prev => ({ ...prev, title: e.target.value }))}
                                            className="md3-textfield-outlined w-full text-body-large font-bold"
                                            placeholder="Willkommen bei den Pfadfindern Wutöschingen!"
                                        />
                                    </div>

                                    <div>
                                        <label className="text-label-medium text-surface-onVariant font-bold block mb-1">
                                            Untertitel
                                        </label>
                                        <input
                                            type="text"
                                            value={intro.subtitle || ''}
                                            onChange={e => setIntro(prev => ({ ...prev, subtitle: e.target.value }))}
                                            className="md3-textfield-outlined w-full text-body-medium"
                                            placeholder="Schön, dass du da bist..."
                                        />
                                    </div>

                                    <div>
                                        <label className="text-label-medium text-surface-onVariant font-bold block mb-1">
                                            Haupttext - Absatz 1
                                        </label>
                                        <textarea
                                            rows={3}
                                            value={intro.mainParagraph1 || ''}
                                            onChange={e => setIntro(prev => ({ ...prev, mainParagraph1: e.target.value }))}
                                            className="md3-textfield-outlined w-full text-body-medium"
                                            placeholder="Wir sind ein brandneues Pfadfinderprojekt..."
                                        />
                                    </div>

                                    <div>
                                        <label className="text-label-medium text-surface-onVariant font-bold block mb-1">
                                            Haupttext - Absatz 2
                                        </label>
                                        <textarea
                                            rows={3}
                                            value={intro.mainParagraph2 || ''}
                                            onChange={e => setIntro(prev => ({ ...prev, mainParagraph2: e.target.value }))}
                                            className="md3-textfield-outlined w-full text-body-medium"
                                            placeholder="Ob Lagerfeuer unter dem Sternenhimmel..."
                                        />
                                    </div>
                                </div>
                            </section>

                            {/* Bund Partner Card */}
                            <section className="md3-card-elevated p-6 bg-surface-containerLowest border border-outlineVariant/30 rounded-3xl space-y-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-title-medium font-bold text-surface-on">Bund & Dachverband Partnerkarte</h3>
                                        <p className="text-body-small text-surface-onVariant mt-1">
                                            Informationen über den übergeordneten Pfadfinderbund (z. B. Horizonte e.V.)
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setIntro(prev => ({
                                            ...prev,
                                            bundCard: { ...prev.bundCard, show: prev.bundCard?.show === false ? true : false }
                                        }))}
                                        className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                                            intro.bundCard?.show !== false
                                                ? 'bg-primary/10 text-primary'
                                                : 'bg-surface-containerHighest text-surface-onVariant'
                                        }`}
                                    >
                                        {intro.bundCard?.show !== false ? 'Karte aktiv' : 'Karte deaktiviert'}
                                    </button>
                                </div>

                                {intro.bundCard?.show !== false && (
                                    <div className="space-y-4 pt-2">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-label-small text-surface-onVariant font-bold block mb-1">Bund Name</label>
                                                <input
                                                    type="text"
                                                    value={intro.bundCard?.title || ''}
                                                    onChange={e => setIntro(prev => ({
                                                        ...prev,
                                                        bundCard: { ...prev.bundCard, title: e.target.value }
                                                    }))}
                                                    className="md3-textfield-outlined w-full text-body-small"
                                                    placeholder="Pfadfinderbund Horizonte e.V."
                                                />
                                            </div>
                                            <div>
                                                <label className="text-label-small text-surface-onVariant font-bold block mb-1">Logo URL</label>
                                                <input
                                                    type="text"
                                                    value={intro.bundCard?.logoUrl || ''}
                                                    onChange={e => setIntro(prev => ({
                                                        ...prev,
                                                        bundCard: { ...prev.bundCard, logoUrl: e.target.value }
                                                    }))}
                                                    className="md3-textfield-outlined w-full text-body-small font-mono text-xs"
                                                    placeholder="https://..."
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-label-small text-surface-onVariant font-bold block mb-1">Bund Beschreibung 1</label>
                                            <textarea
                                                rows={2}
                                                value={intro.bundCard?.description1 || ''}
                                                onChange={e => setIntro(prev => ({
                                                    ...prev,
                                                    bundCard: { ...prev.bundCard, description1: e.target.value }
                                                }))}
                                                className="md3-textfield-outlined w-full text-body-small"
                                            />
                                        </div>

                                        <div>
                                            <label className="text-label-small text-surface-onVariant font-bold block mb-1">Bund Beschreibung 2</label>
                                            <textarea
                                                rows={2}
                                                value={intro.bundCard?.description2 || ''}
                                                onChange={e => setIntro(prev => ({
                                                    ...prev,
                                                    bundCard: { ...prev.bundCard, description2: e.target.value }
                                                }))}
                                                className="md3-textfield-outlined w-full text-body-small"
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-label-small text-surface-onVariant font-bold block mb-1">Button Text</label>
                                                <input
                                                    type="text"
                                                    value={intro.bundCard?.buttonText || ''}
                                                    onChange={e => setIntro(prev => ({
                                                        ...prev,
                                                        bundCard: { ...prev.bundCard, buttonText: e.target.value }
                                                    }))}
                                                    className="md3-textfield-outlined w-full text-body-small"
                                                    placeholder="Mehr über den Bund erfahren"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-label-small text-surface-onVariant font-bold block mb-1">Button Website URL</label>
                                                <input
                                                    type="text"
                                                    value={intro.bundCard?.buttonUrl || ''}
                                                    onChange={e => setIntro(prev => ({
                                                        ...prev,
                                                        bundCard: { ...prev.bundCard, buttonUrl: e.target.value }
                                                    }))}
                                                    className="md3-textfield-outlined w-full text-body-small font-mono text-xs"
                                                    placeholder="https://pbhorizonte.de/"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </section>
                        </div>

                        {/* Sidebar Info */}
                        <div className="space-y-6">
                            <section className="md3-card-filled p-6 bg-secondary-container/30 text-secondary-onContainer rounded-3xl">
                                <h4 className="text-title-small font-bold mb-2">Bund / Verband</h4>
                                <p className="text-body-small leading-relaxed">
                                    Wenn dein Pfadfinderstamm keinem Bund angehört oder die Karte nicht angezeigt werden soll, kannst du sie oben einfach mit einem Klick deaktivieren.
                                </p>
                            </section>
                        </div>
                    </div>
                )}

                {/* ============================================================ */}
                {/* 4. ABOUT & VALUES SECTION TAB */}
                {/* ============================================================ */}
                {activeTab === 'about' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-6">
                            <section className="md3-card-elevated p-6 bg-surface-containerLowest border border-outlineVariant/30 rounded-3xl space-y-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-title-medium font-bold text-surface-on">Werte & "Über uns"</h3>
                                        <p className="text-body-small text-surface-onVariant mt-1">
                                            Definiere die Werte-Karten, die Besuchern zeigen, was Pfadfinden bei euch bedeutet.
                                        </p>
                                    </div>
                                    <button
                                        onClick={addValueCard}
                                        className="md3-btn-tonal flex items-center gap-1.5 text-xs px-3 py-2 rounded-full"
                                    >
                                        <Plus sx={{ fontSize: 16 }} />
                                        <span>Wert hinzufügen</span>
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-label-small text-surface-onVariant font-bold block mb-1">Sektionstitel</label>
                                            <input
                                                type="text"
                                                value={about.title || ''}
                                                onChange={e => setAbout(prev => ({ ...prev, title: e.target.value }))}
                                                className="md3-textfield-outlined w-full text-body-medium font-bold"
                                                placeholder="Was Pfadfinden bei uns bedeutet"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-label-small text-surface-onVariant font-bold block mb-1">Untertitel</label>
                                            <input
                                                type="text"
                                                value={about.subtitle || ''}
                                                onChange={e => setAbout(prev => ({ ...prev, subtitle: e.target.value }))}
                                                className="md3-textfield-outlined w-full text-body-medium"
                                                placeholder="Unsere Werte und Ziele..."
                                            />
                                        </div>
                                    </div>

                                    {/* Value Cards List */}
                                    <div className="pt-4 space-y-4">
                                        <label className="text-label-medium text-surface-on font-bold block">
                                            Werte-Karten ({about.values?.length || 0})
                                        </label>

                                        {about.values?.map((valCard, idx) => {
                                            const colorClasses = getColorClasses(valCard.iconColor);
                                            const isEditing = editingValueIndex === idx;

                                            return (
                                                <div
                                                    key={idx}
                                                    className="p-4 rounded-2xl bg-surface-containerLow border border-outlineVariant/30 space-y-3"
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorClasses.bg} ${colorClasses.text}`}>
                                                                {getIconComponent(valCard.icon, { fontSize: 22 })}
                                                            </div>
                                                            <div>
                                                                <h4 className="text-title-small font-bold text-surface-on">{valCard.title}</h4>
                                                                <p className="text-body-small text-surface-onVariant line-clamp-1">{valCard.description}</p>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => moveValueCard(idx, -1)}
                                                                disabled={idx === 0}
                                                                className="w-8 h-8 rounded-full bg-surface-containerHigh hover:bg-surface-containerHighest disabled:opacity-30 flex items-center justify-center text-xs"
                                                            >
                                                                ▲
                                                            </button>
                                                            <button
                                                                onClick={() => moveValueCard(idx, 1)}
                                                                disabled={idx === about.values.length - 1}
                                                                className="w-8 h-8 rounded-full bg-surface-containerHigh hover:bg-surface-containerHighest disabled:opacity-30 flex items-center justify-center text-xs"
                                                            >
                                                                ▼
                                                            </button>
                                                            <button
                                                                onClick={() => setEditingValueIndex(isEditing ? null : idx)}
                                                                className="md3-btn-tonal text-xs py-1 px-3 rounded-full"
                                                            >
                                                                {isEditing ? 'Schließen' : 'Bearbeiten'}
                                                            </button>
                                                            <button
                                                                onClick={() => deleteValueCard(idx)}
                                                                className="w-8 h-8 rounded-full bg-error-container text-error flex items-center justify-center"
                                                                title="Karte löschen"
                                                            >
                                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Expanded Edit Form */}
                                                    {isEditing && (
                                                        <div className="pt-4 border-t border-outlineVariant/30 space-y-3 bg-surface-containerLowest/50 p-4 rounded-xl">
                                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                                <div>
                                                                    <label className="text-label-small text-surface-onVariant font-bold block mb-1">Titel</label>
                                                                    <input
                                                                        type="text"
                                                                        value={valCard.title}
                                                                        onChange={e => updateValueCard(idx, 'title', e.target.value)}
                                                                        className="md3-textfield-outlined w-full text-body-small"
                                                                    />
                                                                </div>

                                                                <div>
                                                                    <label className="text-label-small text-surface-onVariant font-bold block mb-1">Icon auswählen</label>
                                                                    <select
                                                                        value={valCard.icon}
                                                                        onChange={e => updateValueCard(idx, 'icon', e.target.value)}
                                                                        className="md3-textfield-outlined w-full text-body-small"
                                                                    >
                                                                        {AVAILABLE_VALUE_ICONS.map(i => (
                                                                            <option key={i.id} value={i.id}>{i.label}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>

                                                                <div>
                                                                    <label className="text-label-small text-surface-onVariant font-bold block mb-1">Farbschema</label>
                                                                    <select
                                                                        value={valCard.iconColor || 'primary'}
                                                                        onChange={e => updateValueCard(idx, 'iconColor', e.target.value)}
                                                                        className="md3-textfield-outlined w-full text-body-small"
                                                                    >
                                                                        <option value="primary">Primary (Hauptfarbe)</option>
                                                                        <option value="secondary">Secondary (Akzent)</option>
                                                                        <option value="tertiary">Tertiary (Tertiär)</option>
                                                                    </select>
                                                                </div>
                                                            </div>

                                                            <div>
                                                                <label className="text-label-small text-surface-onVariant font-bold block mb-1">Beschreibung</label>
                                                                <textarea
                                                                    rows={2}
                                                                    value={valCard.description}
                                                                    onChange={e => updateValueCard(idx, 'description', e.target.value)}
                                                                    className="md3-textfield-outlined w-full text-body-small"
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </section>
                        </div>

                        {/* Sidebar Info */}
                        <div className="space-y-6">
                            <section className="md3-card-filled p-6 bg-secondary-container/30 text-secondary-onContainer rounded-3xl">
                                <h4 className="text-title-small font-bold mb-2">Werte vermitteln</h4>
                                <p className="text-body-small leading-relaxed">
                                    Pfadfinder-Werte wie Naturverbundenheit, Teamgeist oder Selbstständigkeit können hier mit prägnanten Texten und passenden Icons präsentiert werden.
                                </p>
                            </section>
                        </div>
                    </div>
                )}

                {/* ============================================================ */}
                {/* 5. SECTION HEADERS TAB */}
                {/* ============================================================ */}
                {activeTab === 'headers' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-6">
                            <section className="md3-card-elevated p-6 bg-surface-containerLowest border border-outlineVariant/30 rounded-3xl space-y-6">
                                <div>
                                    <h3 className="text-title-medium font-bold text-surface-on">Sektionstitel & Untertitel</h3>
                                    <p className="text-body-small text-surface-onVariant mt-1">
                                        Passe die Überschriften der dynamischen Sektionen für deinen Stamm an.
                                    </p>
                                </div>

                                <div className="space-y-6">
                                    {/* Blog Header */}
                                    <div className="p-4 rounded-2xl bg-surface-containerHigh/40 border border-outlineVariant/30 space-y-3">
                                        <h4 className="text-title-small font-bold text-primary">Blog & Neuigkeiten</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <label className="text-label-small text-surface-onVariant block mb-1 font-bold">Titel</label>
                                                <input
                                                    type="text"
                                                    value={sectionHeaders.blog?.title || ''}
                                                    onChange={e => setSectionHeaders(prev => ({
                                                        ...prev,
                                                        blog: { ...prev.blog, title: e.target.value }
                                                    }))}
                                                    className="md3-textfield-outlined w-full text-body-small"
                                                    placeholder="Aktuelles aus dem Stamm"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-label-small text-surface-onVariant block mb-1 font-bold">Untertitel</label>
                                                <input
                                                    type="text"
                                                    value={sectionHeaders.blog?.subtitle || ''}
                                                    onChange={e => setSectionHeaders(prev => ({
                                                        ...prev,
                                                        blog: { ...prev.blog, subtitle: e.target.value }
                                                    }))}
                                                    className="md3-textfield-outlined w-full text-body-small"
                                                    placeholder="Hier findest du Berichte und Neuigkeiten..."
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Team Header */}
                                    <div className="p-4 rounded-2xl bg-surface-containerHigh/40 border border-outlineVariant/30 space-y-3">
                                        <h4 className="text-title-small font-bold text-secondary">👥 Unser Team</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <label className="text-label-small text-surface-onVariant block mb-1 font-bold">Titel</label>
                                                <input
                                                    type="text"
                                                    value={sectionHeaders.team?.title || ''}
                                                    onChange={e => setSectionHeaders(prev => ({
                                                        ...prev,
                                                        team: { ...prev.team, title: e.target.value }
                                                    }))}
                                                    className="md3-textfield-outlined w-full text-body-small"
                                                    placeholder="Unser Team"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-label-small text-surface-onVariant block mb-1 font-bold">Untertitel</label>
                                                <input
                                                    type="text"
                                                    value={sectionHeaders.team?.subtitle || ''}
                                                    onChange={e => setSectionHeaders(prev => ({
                                                        ...prev,
                                                        team: { ...prev.team, subtitle: e.target.value }
                                                    }))}
                                                    className="md3-textfield-outlined w-full text-body-small"
                                                    placeholder="Lerne die Menschen hinter dem Projekt kennen..."
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Events Header */}
                                    <div className="p-4 rounded-2xl bg-surface-containerHigh/40 border border-outlineVariant/30 space-y-3">
                                        <h4 className="text-title-small font-bold text-tertiary">📅 Kommende Termine & Aktionen</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <label className="text-label-small text-surface-onVariant block mb-1 font-bold">Titel</label>
                                                <input
                                                    type="text"
                                                    value={sectionHeaders.events?.title || ''}
                                                    onChange={e => setSectionHeaders(prev => ({
                                                        ...prev,
                                                        events: { ...prev.events, title: e.target.value }
                                                    }))}
                                                    className="md3-textfield-outlined w-full text-body-small"
                                                    placeholder="Kommende Aktionen"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-label-small text-surface-onVariant block mb-1 font-bold">Untertitel</label>
                                                <input
                                                    type="text"
                                                    value={sectionHeaders.events?.subtitle || ''}
                                                    onChange={e => setSectionHeaders(prev => ({
                                                        ...prev,
                                                        events: { ...prev.events, subtitle: e.target.value }
                                                    }))}
                                                    className="md3-textfield-outlined w-full text-body-small"
                                                    placeholder="Sei dabei bei unseren nächsten Gruppenstunden..."
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contact Header */}
                                    <div className="p-4 rounded-2xl bg-surface-containerHigh/40 border border-outlineVariant/30 space-y-3">
                                        <h4 className="text-title-small font-bold text-primary">Kontakt & Schnuppern</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <label className="text-label-small text-surface-onVariant block mb-1 font-bold">Titel</label>
                                                <input
                                                    type="text"
                                                    value={sectionHeaders.contact?.title || ''}
                                                    onChange={e => setSectionHeaders(prev => ({
                                                        ...prev,
                                                        contact: { ...prev.contact, title: e.target.value }
                                                    }))}
                                                    className="md3-textfield-outlined w-full text-body-small"
                                                    placeholder="Schreib uns!"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-label-small text-surface-onVariant block mb-1 font-bold">Untertitel</label>
                                                <input
                                                    type="text"
                                                    value={sectionHeaders.contact?.subtitle || ''}
                                                    onChange={e => setSectionHeaders(prev => ({
                                                        ...prev,
                                                        contact: { ...prev.contact, subtitle: e.target.value }
                                                    }))}
                                                    className="md3-textfield-outlined w-full text-body-small"
                                                    placeholder="Egal ob du reinschnuppern möchtest..."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>

                        {/* Sidebar Info */}
                        <div className="space-y-6">
                            <section className="md3-card-filled p-6 bg-secondary-container/30 text-secondary-onContainer rounded-3xl">
                                <h4 className="text-title-small font-bold mb-2">Individuelle Ansprache</h4>
                                <p className="text-body-small leading-relaxed">
                                    Wähle Überschriften, die zum Ton deines Stammes passen – egal ob förmlich, einladend oder voller Abenteuerlust.
                                </p>
                            </section>
                        </div>
                    </div>
                )}

                {/* ============================================================ */}
                {/* 6. TRIBE IDENTITY & WHITE-LABEL TAB */}
                {/* ============================================================ */}
                {activeTab === 'org' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-6">
                            
                            {/* Browser-Tab & Favicon Branding Section */}
                            <section className="md3-card-elevated p-6 bg-surface-containerLowest border border-outlineVariant/30 rounded-3xl space-y-6">
                                <div>
                                    <h3 className="text-title-medium font-bold text-surface-on flex items-center gap-2">
                                        Browser-Tab & Logo (Favicon)
                                    </h3>
                                    <p className="text-body-small text-surface-onVariant mt-1">
                                        Passe das Icon und die Texte im Browser-Tab für die Website und das Dashboard dynamisch an.
                                    </p>
                                </div>

                                {/* Live Browser Tab Preview */}
                                <div className="p-4 rounded-2xl bg-surface-containerLow/80 border border-outlineVariant/30 space-y-2">
                                    <span className="text-[11px] font-bold text-surface-onVariant uppercase tracking-wider block">
                                        Live-Vorschau der Browser-Tabs
                                    </span>
                                    <div className="flex flex-wrap items-end gap-3 pt-2">
                                        {/* Website Tab Mock */}
                                        <div className="flex items-center gap-2.5 px-3 py-2 bg-surface-containerHighest rounded-t-xl border-t border-x border-outlineVariant/40 shadow-sm min-w-[200px] max-w-[260px]">
                                            <img
                                                src={organization.faviconUrl || '/favicon.ico'}
                                                alt="Favicon"
                                                className="w-4 h-4 rounded object-contain shrink-0"
                                                onError={(e) => { e.target.src = '/favicon.ico'; }}
                                            />
                                            <span className="text-xs font-medium text-surface-on truncate">
                                                {organization.tabTitle || organization.stammName || 'Pfadfinder Wutöschingen'}
                                            </span>
                                            <span className="text-[10px] text-surface-onVariant opacity-60 ml-auto cursor-default">✕</span>
                                        </div>

                                        {/* Dashboard Tab Mock */}
                                        <div className="flex items-center gap-2.5 px-3 py-2 bg-surface-containerHighest/60 rounded-t-xl border-t border-x border-outlineVariant/30 min-w-[200px] max-w-[260px]">
                                            <img
                                                src={organization.faviconUrl || '/favicon.ico'}
                                                alt="Favicon"
                                                className="w-4 h-4 rounded object-contain shrink-0"
                                                onError={(e) => { e.target.src = '/favicon.ico'; }}
                                            />
                                            <span className="text-xs font-medium text-surface-on truncate">
                                                {organization.dashboardTabTitle || (organization.stammName ? `Dashboard | ${organization.stammName}` : 'Dashboard')}
                                            </span>
                                            <span className="text-[10px] text-surface-onVariant opacity-60 ml-auto cursor-default">✕</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Favicon Upload & Controls */}
                                <div className="space-y-3">
                                    <label className="text-label-small text-surface-onVariant font-bold block">
                                        Browser-Tab Icon / Favicon (PNG, ICO, SVG, WEBP)
                                    </label>
                                    
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                        {/* Thumbnail */}
                                        <div className="w-12 h-12 rounded-2xl bg-surface-containerHigh border border-outlineVariant/40 flex items-center justify-center p-2 shrink-0 shadow-sm">
                                            <img
                                                src={organization.faviconUrl || '/favicon.ico'}
                                                alt="Favicon Thumbnail"
                                                className="w-full h-full object-contain"
                                                onError={(e) => { e.target.src = '/favicon.ico'; }}
                                            />
                                        </div>

                                        {/* URL input */}
                                        <div className="flex-1 w-full">
                                            <input
                                                type="text"
                                                value={organization.faviconUrl || ''}
                                                onChange={e => setOrganization(prev => ({ ...prev, faviconUrl: e.target.value }))}
                                                className="md3-textfield-outlined w-full text-body-small font-mono text-xs"
                                                placeholder="https://... oder /favicon.ico"
                                            />
                                        </div>

                                        {/* Upload Button */}
                                        <label className={`md3-btn-tonal flex items-center gap-2 cursor-pointer shrink-0 ${isUploadingFavicon ? 'opacity-50 pointer-events-none' : ''}`}>
                                            <input
                                                type="file"
                                                accept="image/png,image/x-icon,image/vnd.microsoft.icon,image/jpeg,image/svg+xml,image/webp"
                                                onChange={handleFaviconUpload}
                                                className="hidden"
                                                disabled={isUploadingFavicon}
                                            />
                                            {isUploadingFavicon ? (
                                                <RefreshCw className="animate-spin" sx={{ fontSize: 18 }} />
                                            ) : (
                                                <UploadIcon sx={{ fontSize: 18 }} />
                                            )}
                                            <span>{isUploadingFavicon ? 'Lädt...' : 'Hochladen'}</span>
                                        </label>

                                        {/* Reset Button if custom icon set */}
                                        {organization.faviconUrl && (
                                            <button
                                                type="button"
                                                onClick={() => setOrganization(prev => ({ ...prev, faviconUrl: '' }))}
                                                className="md3-btn-text text-error text-xs shrink-0"
                                                title="Auf Standard zurücksetzen"
                                            >
                                                Zurücksetzen
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Titles & Dashboard Subtitle */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-outlineVariant/30">
                                    <div>
                                        <label className="text-label-small text-surface-onVariant font-bold block mb-1">
                                            Website Browser-Tab Titel
                                        </label>
                                        <input
                                            type="text"
                                            value={organization.tabTitle || ''}
                                            onChange={e => setOrganization(prev => ({ ...prev, tabTitle: e.target.value }))}
                                            className="md3-textfield-outlined w-full text-body-small"
                                            placeholder="Pfadfinder Wutöschingen"
                                        />
                                        <span className="text-[10px] text-surface-onVariant opacity-70 mt-1 block">
                                            Wird im Browser-Tab der öffentlichen Website angezeigt.
                                        </span>
                                    </div>

                                    <div>
                                        <label className="text-label-small text-surface-onVariant font-bold block mb-1">
                                            Dashboard Browser-Tab Titel
                                        </label>
                                        <input
                                            type="text"
                                            value={organization.dashboardTabTitle || ''}
                                            onChange={e => setOrganization(prev => ({ ...prev, dashboardTabTitle: e.target.value }))}
                                            className="md3-textfield-outlined w-full text-body-small"
                                            placeholder="Dashboard | Pfadfinder Wutöschingen"
                                        />
                                        <span className="text-[10px] text-surface-onVariant opacity-70 mt-1 block">
                                            Wird im Browser-Tab des Dashboards angezeigt.
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-label-small text-surface-onVariant font-bold block mb-1">
                                        Dashboard Begrüßung / Unterzeile
                                    </label>
                                    <input
                                        type="text"
                                        value={organization.dashboardWelcomeSubtitle || ''}
                                        onChange={e => setOrganization(prev => ({ ...prev, dashboardWelcomeSubtitle: e.target.value }))}
                                        className="md3-textfield-outlined w-full text-body-small"
                                        placeholder="Eingeloggt als"
                                    />
                                    <span className="text-[10px] text-surface-onVariant opacity-70 mt-1 block">
                                        Text unter der „Willkommen, [Name]“-Überschrift im Dashboard (z. B. „Eingeloggt als“ oder Stammesmotto).
                                    </span>
                                </div>
                            </section>

                            <section className="md3-card-elevated p-6 bg-surface-containerLowest border border-outlineVariant/30 rounded-3xl space-y-6">
                                <div>
                                    <h3 className="text-title-medium font-bold text-surface-on">⚜️ Stammes-Identität & White-Label</h3>
                                    <p className="text-body-small text-surface-onVariant mt-1">
                                        Zentrale Angaben über deinen Pfadfinderstamm, Bund, Links und Social Media.
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-label-small text-surface-onVariant font-bold block mb-1">Stammesname (Kurzform)</label>
                                            <input
                                                type="text"
                                                value={organization.stammName || ''}
                                                onChange={e => setOrganization(prev => ({ ...prev, stammName: e.target.value }))}
                                                className="md3-textfield-outlined w-full text-body-small"
                                                placeholder="Pfadfinder Wutöschingen"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-label-small text-surface-onVariant font-bold block mb-1">Stammesname (Offiziell / Langform)</label>
                                            <input
                                                type="text"
                                                value={organization.stammFullName || ''}
                                                onChange={e => setOrganization(prev => ({ ...prev, stammFullName: e.target.value }))}
                                                className="md3-textfield-outlined w-full text-body-small"
                                                placeholder="z. B. Pfadfinder Wutöschingen"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="text-label-small text-surface-onVariant font-bold block mb-1">Bund</label>
                                            <input
                                                type="text"
                                                value={organization.bundName || ''}
                                                onChange={e => setOrganization(prev => ({ ...prev, bundName: e.target.value }))}
                                                className="md3-textfield-outlined w-full text-body-small"
                                                placeholder="Pfadfinderbund Horizonte e.V."
                                            />
                                        </div>
                                        <div>
                                            <label className="text-label-small text-surface-onVariant font-bold block mb-1">Dachverband</label>
                                            <input
                                                type="text"
                                                value={organization.dachverband || ''}
                                                onChange={e => setOrganization(prev => ({ ...prev, dachverband: e.target.value }))}
                                                className="md3-textfield-outlined w-full text-body-small"
                                                placeholder="DPV - Deutscher Pfadfinderverband"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-label-small text-surface-onVariant font-bold block mb-1">Standort / Ort</label>
                                            <input
                                                type="text"
                                                value={organization.location || ''}
                                                onChange={e => setOrganization(prev => ({ ...prev, location: e.target.value }))}
                                                className="md3-textfield-outlined w-full text-body-small"
                                                placeholder="Wutöschingen"
                                            />
                                        </div>
                                    </div>

                                    <div className="pt-4 border-t border-outlineVariant/30 space-y-4">
                                        <h4 className="text-title-small font-bold text-surface-on">🌐 Links & Social Media</h4>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-label-small text-surface-onVariant font-bold block mb-1">Kontakt E-Mail</label>
                                                <input
                                                    type="email"
                                                    value={organization.contactEmail || ''}
                                                    onChange={e => setOrganization(prev => ({ ...prev, contactEmail: e.target.value }))}
                                                    className="md3-textfield-outlined w-full text-body-small"
                                                    placeholder="info@pfadfinder-wutoeschingen.de"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-label-small text-surface-onVariant font-bold block mb-1">Mitgliedsantrag / CampFlow URL</label>
                                                <input
                                                    type="text"
                                                    value={organization.registrationUrl || ''}
                                                    onChange={e => setOrganization(prev => ({ ...prev, registrationUrl: e.target.value }))}
                                                    className="md3-textfield-outlined w-full text-body-small font-mono text-xs"
                                                    placeholder="https://on.campflow.de/..."
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-label-small text-surface-onVariant font-bold block mb-1">Instagram Profil URL</label>
                                                <input
                                                    type="text"
                                                    value={organization.instagramUrl || ''}
                                                    onChange={e => setOrganization(prev => ({ ...prev, instagramUrl: e.target.value }))}
                                                    className="md3-textfield-outlined w-full text-body-small font-mono text-xs"
                                                    placeholder="https://www.instagram.com/..."
                                                />
                                            </div>
                                            <div>
                                                <label className="text-label-small text-surface-onVariant font-bold block mb-1">WhatsApp Gruppe URL</label>
                                                <input
                                                    type="text"
                                                    value={organization.whatsappUrl || ''}
                                                    onChange={e => setOrganization(prev => ({ ...prev, whatsappUrl: e.target.value }))}
                                                    className="md3-textfield-outlined w-full text-body-small font-mono text-xs"
                                                    placeholder="https://chat.whatsapp.com/..."
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pt-4 border-t border-outlineVariant/30 space-y-4">
                                        <h4 className="text-title-small font-bold text-surface-on">Footer Texte</h4>
                                        <div>
                                            <label className="text-label-small text-surface-onVariant font-bold block mb-1">Footer Beschreibung</label>
                                            <textarea
                                                rows={2}
                                                value={organization.footerText || ''}
                                                onChange={e => setOrganization(prev => ({ ...prev, footerText: e.target.value }))}
                                                className="md3-textfield-outlined w-full text-body-small"
                                                placeholder="Wir entdecken die Welt..."
                                            />
                                        </div>
                                        <div>
                                            <label className="text-label-small text-surface-onVariant font-bold block mb-1">Footer Slogan / Tagline</label>
                                            <input
                                                type="text"
                                                value={organization.footerTagline || ''}
                                                onChange={e => setOrganization(prev => ({ ...prev, footerTagline: e.target.value }))}
                                                className="md3-textfield-outlined w-full text-body-small"
                                                placeholder="Handgemacht mit ❤️ für die Jugend von der Jugend."
                                            />
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>

                        {/* Sidebar Info */}
                        <div className="space-y-6">
                            <section className="md3-card-filled p-6 bg-secondary-container/30 text-secondary-onContainer rounded-3xl">
                                <h4 className="text-title-small font-bold mb-2">Multi-Stamm Bereitstellung</h4>
                                <p className="text-body-small leading-relaxed">
                                    Diese Angaben fließen automatisch in die Navigation, das Impressum, den Footer und Bobs Kontext ein. Wenn die Plattform an einen anderen Stamm übergeben wird, müssen nur diese Felder ausgefüllt werden!
                                </p>
                            </section>
                        </div>
                    </div>
                )}

                {/* ============================================================ */}
                {/* 7. IMAGES TAB */}
                {/* ============================================================ */}
                {activeTab === 'images' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-6">
                            {/* Slideshow Preview */}
                            <section className="md3-card-elevated overflow-hidden bg-surface-containerLowest border border-outlineVariant/30 rounded-3xl">
                                <div className="p-6 border-b border-outlineVariant/30 bg-surface-containerLow">
                                    <h3 className="text-title-medium font-bold flex items-center gap-2">
                                        <Globe sx={{ fontSize: 20 }} className="text-primary" />
                                        Hero-Diashow ({heroImages.length} Bilder)
                                    </h3>
                                    <p className="text-body-small text-surface-onVariant">Wähle mehrere Bilder für eine automatische Diashow auf der Startseite.</p>
                                </div>

                                {/* Preview */}
                                <div className="relative aspect-video bg-surface-containerHigh">
                                    {heroImages.length > 0 ? (
                                        <SlideshowPreview images={heroImages} interval={slideshowInterval} getAssetUrl={getAssetUrl} />
                                    ) : (
                                        <div className="w-full h-full flex flex-col items-center justify-center p-12 text-center">
                                            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
                                                <UploadIcon sx={{ fontSize: 32 }} />
                                            </div>
                                            <h4 className="text-title-medium font-bold mb-1">Keine Bilder ausgewählt</h4>
                                            <p className="text-body-small text-surface-onVariant mb-6">Wähle Bilder aus oder lade welche hoch.</p>
                                        </div>
                                    )}
                                    {isUploading && (
                                        <div className="absolute inset-0 bg-surface/60 backdrop-blur-sm flex flex-col items-center justify-center z-20">
                                            <RefreshCw className="animate-spin text-primary mb-3" sx={{ fontSize: 40 }} />
                                            <span className="text-label-large font-bold">Wird hochgeladen...</span>
                                        </div>
                                    )}
                                </div>

                                <div className="p-6 space-y-4">
                                    {/* Interval Setting */}
                                    <div className="flex items-center gap-4">
                                        <label className="text-label-medium text-surface-onVariant font-bold whitespace-nowrap">Wechsel-Intervall</label>
                                        <input type="range" min="3" max="15" value={slideshowInterval} onChange={e => setSlideshowInterval(Number(e.target.value))}
                                            className="flex-1 accent-primary" />
                                        <span className="text-label-large font-bold text-primary w-12 text-right">{slideshowInterval}s</span>
                                    </div>

                                    {/* Selected Images */}
                                    {heroImages.length > 0 && (
                                        <div className="space-y-2">
                                            <label className="text-label-medium text-surface-onVariant font-bold">Ausgewählte Bilder</label>
                                            <div className="flex gap-2 overflow-x-auto pb-2">
                                                {heroImages.map((url, i) => (
                                                    <div key={i} className="relative flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden border border-primary group">
                                                        <img src={getAssetUrl ? getAssetUrl(url) : url} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" />
                                                        <button onClick={() => setHeroImages(prev => prev.filter((_, j) => j !== i))}
                                                            className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                            <DeleteIcon sx={{ fontSize: 18 }} className="text-white" />
                                                        </button>
                                                        <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] text-center py-0.5">{i + 1}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Upload */}
                                    <label className="md3-btn-outlined cursor-pointer h-12 gap-2 inline-flex items-center">
                                        <UploadIcon sx={{ fontSize: 18 }} />
                                        <span>Datei hochladen</span>
                                        <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} disabled={isUploading} />
                                    </label>
                                </div>
                            </section>

                            {/* Image Sources */}
                            <ImagePicker title="Aus hochgeladenen Bildern" description="Klicke auf Bilder um sie zur Diashow hinzuzufügen oder zu entfernen."
                                images={uploadedImages.map(f => ({ id: f.key, url: f.url || f.key, label: f.name }))}
                                selectedUrls={heroImages} onToggle={toggleImageSelection}
                                search={uploadedSearch} onSearchChange={setUploadedSearch}
                                isLoading={isLoadingUploadedImages} emptyText="Keine hochgeladenen Bilder gefunden."
                                getAssetUrl={getAssetUrl} />

                            <ImagePicker title="Bilder aus Blogbeiträgen" description="Blogbilder für die Diashow auswählen."
                                images={blogImages.map(p => ({ id: p._id, url: p.imageUrl, label: p.title }))}
                                selectedUrls={heroImages} onToggle={toggleImageSelection}
                                search={blogSearch} onSearchChange={setBlogSearch}
                                isLoading={isLoadingBlogImages} emptyText="Keine Blogbilder gefunden."
                                getAssetUrl={getAssetUrl} />
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-6">
                            <section className="md3-card-filled p-6 bg-secondary-container/30 text-secondary-onContainer rounded-3xl">
                                <h4 className="text-title-small font-bold mb-2">Diashow</h4>
                                <p className="text-body-small">
                                    Wähle mehrere Bilder aus — sie werden auf der Startseite automatisch als flüssige Diashow angezeigt.
                                </p>
                            </section>
                        </div>
                    </div>
                )}

                {/* ============================================================ */}
                {/* 8. COLOR GENERATOR TAB */}
                {/* ============================================================ */}
                {activeTab === 'colors' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-6">
                            {/* Color Picker */}
                            <section className="md3-card-elevated overflow-hidden bg-surface-containerLowest border border-outlineVariant/30 rounded-3xl">
                                <div className="p-6 border-b border-outlineVariant/30 bg-surface-containerLow">
                                    <h3 className="text-title-medium font-bold">Material You Farbgenerator</h3>
                                    <p className="text-body-small text-surface-onVariant">
                                        Wähle eine Seed-Farbe und generiere ein vollständiges Material Design 3 Farbschema.
                                    </p>
                                </div>

                                <div className="p-6 space-y-6">
                                    <div className="flex items-end gap-4">
                                        <div className="space-y-2">
                                            <label className="text-label-medium text-surface-onVariant font-bold">Seed-Farbe</label>
                                            <div className="flex gap-3 items-center">
                                                <input type="color" value={seedColor} onChange={e => setSeedColor(e.target.value)}
                                                    className="w-14 h-14 rounded-xl cursor-pointer border-2 border-outlineVariant" />
                                                <input type="text" value={seedColor} onChange={e => setSeedColor(e.target.value)}
                                                    className="md3-textfield-outlined h-14 w-32 text-body-large font-mono uppercase" maxLength={7} />
                                            </div>
                                        </div>
                                        <button onClick={handleGenerateColors} className="md3-btn-filled h-14 px-6">
                                            Generieren
                                        </button>
                                    </div>

                                    {/* Quick Presets */}
                                    <div className="space-y-2">
                                        <label className="text-label-medium text-surface-onVariant font-bold">Vorlagen</label>
                                        <div className="flex flex-wrap gap-2">
                                            {[
                                                { color: '#009640', name: 'Pfadfinder Grün' },
                                                { color: '#B33B15', name: 'Warm Orange' },
                                                { color: '#1565C0', name: 'Blau' },
                                                { color: '#6A1B9A', name: 'Lila' },
                                                { color: '#C62828', name: 'Rot' },
                                                { color: '#00838F', name: 'Teal' },
                                                { color: '#4E342E', name: 'Braun' },
                                                { color: '#37474F', name: 'Blaugrau' },
                                            ].map(preset => (
                                                <button key={preset.color} onClick={() => { setSeedColor(preset.color); }}
                                                    className={`flex items-center gap-2 px-3 h-8 rounded-full border text-label-large transition-all ${seedColor === preset.color ? 'border-primary bg-primary/10' : 'border-outlineVariant hover:bg-surface-containerHigh'}`}>
                                                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.color }} />
                                                    <span className="text-label-small">{preset.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {savedTheme && (
                                        <div className="flex items-center gap-3 p-3 rounded-xl bg-primary/5 border border-primary/20">
                                            <div className="w-6 h-6 rounded-full" style={{ backgroundColor: savedTheme.seed }} />
                                            <span className="text-body-small text-surface-onVariant">
                                                Aktives Theme: <strong className="text-primary">{savedTheme.seed}</strong>
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </section>

                            {/* Preview */}
                            {generatedTheme && (
                                <section className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-title-medium font-bold">Vorschau</h3>
                                        <div className="flex gap-2">
                                            {['light', 'dark'].map(mode => (
                                                <button key={mode} onClick={() => setColorPreviewMode(mode)}
                                                    className={`px-4 h-8 rounded-full text-label-large font-medium transition-all ${colorPreviewMode === mode ? 'bg-primary text-primary-on' : 'bg-surface-containerHigh text-surface-on'}`}>
                                                    {mode === 'light' ? 'Light' : 'Dark'}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <ColorThemePreview theme={generatedTheme} previewMode={colorPreviewMode} />
                                </section>
                            )}
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-6">
                            <section className="md3-card-filled p-6 bg-secondary-container/30 text-secondary-onContainer rounded-3xl">
                                <h4 className="text-title-small font-bold mb-2">So funktioniert's</h4>
                                <ol className="text-body-small space-y-2 list-decimal list-inside">
                                    <li>Wähle eine <strong>Seed-Farbe</strong> oder eine Vorlage</li>
                                    <li>Klicke <strong>Generieren</strong> für die Vorschau</li>
                                    <li>Prüfe alle UI-Elemente in Light & Dark</li>
                                    <li>Klicke <strong>Speichern</strong> um das Theme zu aktivieren</li>
                                </ol>
                            </section>
                            <section className="md3-card-outlined p-6 rounded-3xl">
                                <h4 className="text-title-small font-bold mb-2">Material You</h4>
                                <p className="text-body-small text-surface-onVariant">
                                    Basierend auf Googles Material Color Utilities wird aus einer einzigen Farbe
                                    ein vollständiges Farbschema generiert.
                                </p>
                            </section>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

// ============================================================
// Slideshow Preview Component
// ============================================================
const SlideshowPreview = ({ images, interval, getAssetUrl }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        if (images.length <= 1) return;
        const timer = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % images.length);
        }, interval * 1000);
        return () => clearInterval(timer);
    }, [images.length, interval]);

    return (
        <div className="relative w-full h-full">
            {images.map((url, i) => (
                <img key={url} src={getAssetUrl ? getAssetUrl(url) : url} alt={`Slide ${i + 1}`}
                    className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
                    style={{ opacity: i === currentIndex ? 1 : 0 }} />
            ))}
            {images.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                    {images.map((_, i) => (
                        <button key={i} onClick={() => setCurrentIndex(i)}
                            className={`w-2.5 h-2.5 rounded-full transition-all ${i === currentIndex ? 'bg-white scale-125' : 'bg-white/50'}`} />
                    ))}
                </div>
            )}
        </div>
    );
};

// ============================================================
// Reusable Image Picker Component
// ============================================================
const ImagePicker = ({ title, description, images, selectedUrls, onToggle, search, onSearchChange, isLoading, emptyText, getAssetUrl }) => (
    <section className="md3-card-outlined p-6 bg-surface-containerLow border border-outlineVariant/20 rounded-3xl">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-4">
            <div>
                <h4 className="text-title-small font-bold text-surface-on mb-1">{title}</h4>
                <p className="text-body-small text-surface-onVariant">{description}</p>
            </div>
            <div className="flex-1 min-w-0">
                <input type="text" value={search} onChange={e => onSearchChange(e.target.value)}
                    className="md3-textfield-outlined h-12 w-full text-body-medium" placeholder="Suchen..." />
            </div>
        </div>
        {isLoading ? (
            <div className="py-10 text-center text-surface-onVariant">Lade Bilder...</div>
        ) : images.length === 0 ? (
            <div className="py-10 text-center text-surface-onVariant">{emptyText}</div>
        ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {images.map(img => {
                    const isSelected = selectedUrls.includes(img.url);
                    const resolvedSrc = getAssetUrl ? getAssetUrl(img.url) : img.url;
                    return (
                        <button type="button" key={img.id} onClick={() => onToggle(img.url)}
                            className={`group rounded-3xl overflow-hidden border transition-shadow duration-200 ${isSelected ? 'border-primary shadow-elevation-4 ring-2 ring-primary/30' : 'border-outlineVariant/30 hover:shadow-elevation-2'}`}>
                            <div className="relative aspect-video bg-surface-containerHigh">
                                <img src={resolvedSrc} alt={img.label} className="w-full h-full object-cover" />
                                {isSelected && (
                                    <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary text-primary-on flex items-center justify-center text-xs font-bold">
                                        ✓
                                    </div>
                                )}
                            </div>
                            <div className="p-3 text-left">
                                <p className="text-label-small text-surface-on truncate">{img.label}</p>
                            </div>
                        </button>
                    );
                })}
            </div>
        )}
    </section>
);

export default HomepageSettings;
